<template>
  <div class="cfm">
    <span class="cfm-label">{{ label }}</span>
    <i
      v-if="sortField === field"
      class="bi cfm-sort-indicator"
      :class="sortAsc ? 'bi-arrow-up' : 'bi-arrow-down'"
      aria-hidden="true"
    ></i>
    <button
      v-if="sortable || filterable"
      ref="triggerRef"
      type="button"
      class="cfm-trigger"
      :class="{ 'is-active': hasActiveFilter }"
      :aria-label="`Ordenar y filtrar ${label}`"
      :aria-expanded="open"
      @click="toggle"
    >
      <i class="bi" :class="hasActiveFilter ? 'bi-funnel-fill' : 'bi-funnel'"></i>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="panelRef"
        class="cfm-panel"
        :style="panelStyle"
        role="menu"
      >
        <div v-if="sortable" class="cfm-sort-group">
          <button type="button" class="cfm-option" @click="applySort(true)">
            <i class="bi" :class="sortIconAsc"></i>
            <span>{{ sortLabelAsc }}</span>
          </button>
          <button type="button" class="cfm-option" @click="applySort(false)">
            <i class="bi" :class="sortIconDesc"></i>
            <span>{{ sortLabelDesc }}</span>
          </button>
        </div>

        <template v-if="filterable">
          <div v-if="sortable" class="cfm-divider"></div>

          <div v-if="uniqueValues.length > VALUE_SEARCH_THRESHOLD" class="cfm-search">
            <i class="bi bi-search" aria-hidden="true"></i>
            <input
              v-model="valueQuery"
              type="text"
              class="cfm-search-input"
              placeholder="Buscar valor..."
            />
          </div>

          <label class="cfm-item cfm-item--all">
            <input
              type="checkbox"
              :checked="allChecked"
              :indeterminate.prop="someChecked && !allChecked"
              @change="toggleAll"
            />
            <span>(Seleccionar todos)</span>
          </label>

          <div class="cfm-list">
            <label v-for="v in visibleValues" :key="v.value" class="cfm-item">
              <input
                type="checkbox"
                :checked="localSelected.has(v.value)"
                @change="toggleValue(v.value)"
              />
              <span class="cfm-item-label">{{ v.value }}</span>
              <span class="cfm-item-count">{{ v.count }}</span>
            </label>
            <p v-if="!visibleValues.length" class="cfm-empty">Sin coincidencias</p>
          </div>

          <div class="cfm-footer">
            <button
              type="button"
              class="cfm-clear"
              :disabled="!hasActiveFilter"
              @click="clearFilter"
            >
              <i class="bi bi-x-circle"></i> Limpiar filtro
            </button>
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from "vue";

const props = defineProps({
  field: { type: String, required: true },
  label: { type: String, required: true },
  items: { type: Array, default: () => [] },
  type: { type: String, default: "text" }, // 'text' | 'number' | 'date'
  sortable: { type: Boolean, default: true },
  filterable: { type: Boolean, default: true },
  sortField: { type: String, default: "" },
  sortAsc: { type: Boolean, default: true },
  modelValue: { type: Set, default: () => new Set() },
});

const emit = defineEmits(["update:modelValue", "update:sort"]);

// Valor centinela: representa "el usuario desmarcó todo" sin confundirse
// con Set vacío = "sin filtro" (convención que usa el predicado del padre).
const NONE_SENTINEL = Symbol("cfm-none");

// El buscador interno solo tiene sentido cuando hay muchos valores para
// hojear; con pocos (p. ej. Activo/Inactivo) solo estorba.
const VALUE_SEARCH_THRESHOLD = 8;

const open = ref(false);
const valueQuery = ref("");
const localSelected = ref(new Set());
const triggerRef = ref(null);
const panelRef = ref(null);
const panelStyle = ref({});

const hasActiveFilter = computed(() => props.modelValue.size > 0);

const uniqueValues = computed(() => {
  const counts = new Map();
  for (const item of props.items) {
    const raw = item[props.field];
    if (raw === null || raw === undefined || raw === "") continue;
    const key = String(raw);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value, "es"));
});

const visibleValues = computed(() => {
  const q = valueQuery.value.trim().toLowerCase();
  if (!q) return uniqueValues.value;
  return uniqueValues.value.filter((v) => v.value.toLowerCase().includes(q));
});

const allChecked = computed(
  () => uniqueValues.value.length > 0 && localSelected.value.size === uniqueValues.value.length
);
const someChecked = computed(() => localSelected.value.size > 0);

const sortIconAsc = computed(() => {
  if (props.type === "number") return "bi-sort-numeric-down";
  if (props.type === "date") return "bi-sort-down";
  return "bi-sort-alpha-down";
});
const sortIconDesc = computed(() => {
  if (props.type === "number") return "bi-sort-numeric-up";
  if (props.type === "date") return "bi-sort-up";
  return "bi-sort-alpha-up";
});
const sortLabelAsc = computed(() =>
  props.type === "text" ? "Ordenar A → Z" : "Menor a mayor"
);
const sortLabelDesc = computed(() =>
  props.type === "text" ? "Ordenar Z → A" : "Mayor a menor"
);

function syncFromModel() {
  if (props.modelValue.size === 1 && props.modelValue.has(NONE_SENTINEL)) {
    localSelected.value = new Set();
  } else if (props.modelValue.size === 0) {
    localSelected.value = new Set(uniqueValues.value.map((v) => v.value));
  } else {
    localSelected.value = new Set(props.modelValue);
  }
}

function emitSelection() {
  if (localSelected.value.size === uniqueValues.value.length) {
    emit("update:modelValue", new Set());
  } else if (localSelected.value.size === 0) {
    emit("update:modelValue", new Set([NONE_SENTINEL]));
  } else {
    emit("update:modelValue", new Set(localSelected.value));
  }
}

function toggleValue(value) {
  const next = new Set(localSelected.value);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  localSelected.value = next;
  emitSelection();
}

function toggleAll() {
  localSelected.value = allChecked.value
    ? new Set()
    : new Set(uniqueValues.value.map((v) => v.value));
  emitSelection();
}

function clearFilter() {
  valueQuery.value = "";
  localSelected.value = new Set(uniqueValues.value.map((v) => v.value));
  emit("update:modelValue", new Set());
}

function applySort(asc) {
  emit("update:sort", { field: props.field, asc });
  close();
}

function positionPanel() {
  const trigger = triggerRef.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const panelWidth = 240;
  const left = Math.min(rect.left, window.innerWidth - panelWidth - 8);
  panelStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 6}px`,
    left: `${Math.max(8, left)}px`,
    width: `${panelWidth}px`,
  };
}

function onOutsideEvent(e) {
  if (
    panelRef.value &&
    !panelRef.value.contains(e.target) &&
    triggerRef.value &&
    !triggerRef.value.contains(e.target)
  ) {
    close();
  }
}

function onScroll(e) {
  // Ignora el scroll interno de la propia lista (.cfm-list) — solo debe
  // cerrar cuando lo que se mueve es la página o el contenedor detrás.
  if (panelRef.value && e.target && panelRef.value.contains(e.target)) return;
  close();
}

function onKeydown(e) {
  if (e.key === "Escape") close();
}

function toggle() {
  if (open.value) {
    close();
  } else {
    syncFromModel();
    valueQuery.value = "";
    open.value = true;
    nextTick(() => {
      positionPanel();
      window.addEventListener("mousedown", onOutsideEvent, true);
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onScroll);
      window.addEventListener("keydown", onKeydown);
    });
  }
}

function close() {
  if (!open.value) return;
  open.value = false;
  window.removeEventListener("mousedown", onOutsideEvent, true);
  window.removeEventListener("scroll", onScroll, true);
  window.removeEventListener("resize", onScroll);
  window.removeEventListener("keydown", onKeydown);
}

onBeforeUnmount(close);
</script>

<style scoped>
.cfm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cfm-sort-indicator {
  font-size: 0.7rem;
  color: var(--d-green);
}

.cfm-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: var(--d-text-muted);
  font-size: 0.72rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.cfm-trigger:hover {
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
}

.cfm-trigger.is-active {
  color: var(--d-green);
}
</style>

<style>
/* El panel se teletransporta a <body>, fuera del scope del componente. */
.cfm-panel {
  z-index: 2000;
  background: #fff;
  border: 1px solid var(--d-border);
  border-radius: var(--d-radius-md);
  box-shadow: var(--d-elevation-3);
  padding: var(--d-space-2);
  font-size: 0.85rem;
  color: var(--d-brown);
}

.cfm-sort-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: var(--d-space-2);
}

.cfm-option {
  display: flex;
  align-items: center;
  gap: var(--d-space-2);
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 6px var(--d-space-2);
  border-radius: var(--d-radius-sm);
  color: var(--d-brown);
  cursor: pointer;
  font-size: 0.82rem;
}

.cfm-option:hover {
  background: rgba(96, 108, 56, 0.1);
}

.cfm-divider {
  height: 1px;
  background: var(--d-border);
  margin: var(--d-space-1) 0 var(--d-space-2);
}

.cfm-search {
  position: relative;
  margin-bottom: var(--d-space-2);
}

.cfm-search i {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-text-soft);
  font-size: 0.75rem;
}

.cfm-search-input {
  width: 100%;
  height: 30px;
  border: 1px solid var(--d-border);
  border-radius: var(--d-radius-sm);
  padding: 0 8px 0 28px;
  font-size: 0.8rem;
  color: var(--d-brown);
  outline: none;
}

.cfm-search-input:focus {
  border-color: var(--d-green);
}

.cfm-item {
  display: flex;
  align-items: center;
  gap: var(--d-space-2);
  padding: 5px var(--d-space-2);
  border-radius: var(--d-radius-sm);
  cursor: pointer;
  font-size: 0.82rem;
}

.cfm-item:hover {
  background: rgba(96, 108, 56, 0.08);
}

.cfm-item--all {
  font-weight: 700;
  border-bottom: 1px solid var(--d-border);
  border-radius: 0;
  margin-bottom: 2px;
}

.cfm-item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cfm-item-count {
  color: var(--d-text-soft);
  font-size: 0.72rem;
}

.cfm-list {
  max-height: 220px;
  overflow-y: auto;
}

.cfm-empty {
  margin: 0;
  padding: var(--d-space-2);
  text-align: center;
  color: var(--d-text-soft);
  font-size: 0.8rem;
}

.cfm-footer {
  border-top: 1px solid var(--d-border);
  margin-top: var(--d-space-2);
  padding-top: var(--d-space-2);
}

.cfm-clear {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--d-brown);
  font-weight: 600;
  font-size: 0.8rem;
  padding: 6px;
  border-radius: var(--d-radius-sm);
  cursor: pointer;
}

.cfm-clear:hover:not(:disabled) {
  background: rgba(160, 34, 8, 0.08);
  color: #d32f2f;
}

.cfm-clear:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
